<template>
    <div class="statistics">
      <div class="d-flex justify-content-end">
        <button
          class="ui-action-button"
          @click="save"
        >
          {{ $t('global.saveChanges') }}
          <b-spinner class="ml-1" small label="loading" v-if="isUpdating"></b-spinner>
        </button>
      </div>

      <!-- subcategories -->
      <div :key="`statistics__${subcategoryKey}`">

        <div class="statistics__subcategory"
          v-for="(subcategory, index) in subcategories"
          :key="`statistics__${subcategory.name}__${subcategoryKey}`"
        >
          <label class="text-black">{{ $t(`integrityCompliance.integrity.${subcategory.name}`) }}</label>
          <div class="wysiwyg-container">
            <Wysiwyg
              :articleBody="subcategory.originalDescription"
              :includeMediaContent="false"
              @updateBody="onUpdatedBody($event, index)"
              :placeholder="$t('integrityCompliance.body_ph')"
            />
            <div class="ui-input-error-msg">
              <span v-if="$v.subcategories.$each[index].$error">{{ $t("global.inputs.required") }}</span>
            </div>
          </div>

          <!-- subcategory files -->
          <FileUploaderByYear
            :key="`${subcategory.name}_${subcategoryKey}`"
            :subcategory="subcategory"
            :semestersRequired="subcategory.semestersRequired"
            @updatedFiles="onUpdatedFiles"
          />
        </div>

      </div>
    </div>
</template>

<script>
const Wysiwyg = () => import("@/components/Wysiwyg");
const FileUploaderByYear = () => import("@/components/IntegrityCompliance/FileUploaderByYear");
import { integrityKey, integrityComplianceKey, ICfilesName, yearOptions } from "@/lib/constants.js";
import { validateBody } from '@/lib/utils.js';

import API_SERVICE from "@/lib/api/IntegrityCompliance.js";
const api = new API_SERVICE();

import S3_Handler from "@/lib/api/s3_handler";
const s3 = new S3_Handler();

export default {
  inject: ['IC_getMainCategory'], // data from main category "Integrity"
  props: {
    detail: {
      type: Object,
      default: () => { // this contains data of "events" subcategory
        return {
          id: 0, // number
          name: '',
          title: '',
          description: { ops: []},
          is_parent: false // boolean
        }
      }
    }
  },
  components: {
    Wysiwyg,
    FileUploaderByYear
  },
  computed: {
    deafaultYears () {
      return yearOptions()
    },
    subcategoriesDefaultConfiguration () {
      return {
        [integrityKey.sanctions]: {
          semestersRequired: true,
          order: 1
        },
        [integrityKey.donations]: {
          semestersRequired: false,
          order: 2
        }
      }
    }
  },
  data: function () {
    return {
      isUpdating: false,
      subcategoryKey: new Date().getTime(),
      subcategories: [], //  "donations" and "sanctions" subcategories
      subcategoryFiles: {}, // registering all files added from subcategories
      integrityKey
    }
  },
  validations: {
    subcategories: {
      $each: {
        description: {
          isRequired: function(data) {
            return validateBody(data)
          }
        }
      }
    }
  },
  async mounted () {
    this.categoryData = this.IC_getMainCategory();
    await this.getSubCategories(this.detail.id);
    await this.setYearsAndSemesterBySubcategories(this.subcategories);
  },
  methods: {
    async getSubCategories (subcategoryId) {
      if (!subcategoryId) return
      try {
        this.isLoading = true;
        const { subcategories } = await api.getSubcategory(subcategoryId); // subcategory Statistics
        if (subcategories && subcategories.length) {
          this.subcategories = subcategories.map(subcategory => {
            const updated = {
              ...subcategory,
              originalDescription: subcategory.description,
              order: this.subcategoriesDefaultConfiguration[subcategory.name].order
            }
            const subcategoryDefaultConfiguration = this.subcategoriesDefaultConfiguration[subcategory.name];
            if (subcategoryDefaultConfiguration) {
              updated.semestersRequired = Boolean(subcategoryDefaultConfiguration.semestersRequired);
            }
            return updated;
          }).sort((a, b) => a.order - b.order);
          this.subcategoryKey = new Date().getTime();
        } else {
          this.$store.commit("global/SET_ALERT", {
            msg: this.$t('integrityCompliance.integrity.statistics_getError'),
            show: true,
            type: "error"
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.integrity.statistics_getError'),
          show: true,
          type: "error"
        });
      } finally {
        this.isLoading = false;
      }
    },
    async setYearsAndSemesterBySubcategories (subcategories) {
      try {
        const subcateogriesYearsAndSemester = {};
        for (const subcategory of subcategories) {
          const yearsAndSemesters = await api.getYearsAndSemestersBySubcategory(subcategory.id)
          let years = [];
          let updatedSemesters = null;

          if (yearsAndSemesters.length) {
            years = yearsAndSemesters.map(({ year }) => year)
            if (this.subcategoryRequiresSemesters(subcategory.name)) {
              updatedSemesters = {};
              yearsAndSemesters.forEach(({ year, semesters }) => {
                if (semesters && semesters.length) {
                  updatedSemesters[year] = semesters.map(({ semester }) => semester)
                }
              })
            }
          }

          subcateogriesYearsAndSemester[subcategory.name] = {
            years,
            semesters: updatedSemesters
          };
        }

        const originalSubcategories = [...this.subcategories];
        this.subcategories = originalSubcategories.map(subcategory => {
          return {
            ...subcategory,
            years: subcateogriesYearsAndSemester[subcategory.name].years,
            semesters: subcateogriesYearsAndSemester[subcategory.name].semesters
          }
        });
      } catch (error) {
        throw error;
      }
    },
    subcategoryRequiresSemesters (subcategoriName) {
      return Boolean(
        this.subcategoriesDefaultConfiguration[subcategoriName] &&
        this.subcategoriesDefaultConfiguration[subcategoriName].semestersRequired
      );
    },
    onUpdatedBody (body, index) {
      this.subcategories[index].description = body
    },
    onUpdatedFiles (data) {
      const { subcategoryId, subcategoryName, subcategoryFiles } = data;
      this.subcategoryFiles[subcategoryName] = {
        subcategoryId,
        files: subcategoryFiles.filter(subcategoryFile => subcategoryFile.file)
      }
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$error) return;

      try {
        this.isUpdating = true;

        await this.saveSubCategoriesDetail(this.subcategories);
        await this.saveSubcategoriesFiles();

        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('global.successSaved'),
          show: true,
          type: "success"
        });
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.integrity.statistics_saveError'),
          show: true,
          type: "error"
        });
      } finally {
        const updatedSubcategories = this.subcategories.map(subcategory => {
          const updated = {
            ...subcategory,
            originalDescription: subcategory.description
          }
         return updated;
        });
        this.subcategories = [...updatedSubcategories];
        this.subcategoryKey = new Date().getTime();
        this.isUpdating = false;
      }
    },
    async saveSubCategoriesDetail (subcategories = []) {
      try {
        for (const subcategory of subcategories) {
          const existingYears = subcategory.years && subcategory.years.length
            ? subcategory.years
            : [];
          const existingSemesters = subcategory.semesters && Object.keys(subcategory.semesters).length
            ? subcategory.semesters
            : {};

          const { years, semesters } = this.getYearsAndSemestersFromFiles(subcategory.name)
          const params = {
            description: subcategory.description,
            is_parent: false,
            parent_id: this.detail.id
          }

          if (years && years.length) {
            params.years = Array.from(new Set([...existingYears, ...years]));
          }

          if (this.subcategoryRequiresSemesters(subcategory.name) && semesters) { //update new semesters added
              const yearsToAdd = [...params.years];
              const semestersParams = {}

              for (const year of yearsToAdd) {
                const originalSemesters =  existingSemesters[year]
                  ? existingSemesters[year]
                  : []
                const newSemesters = semesters[year]
                  ? semesters[year]
                  : []
                const semestersToAdd = Array.from(new Set([...originalSemesters, ...newSemesters]));
                if (semestersToAdd.length) {
                  semestersParams[year] = semestersToAdd;
                }
              }

              params.semesters = semestersParams;
          }
          await this.saveSubcategory(subcategory.id, subcategory.name, params)
        }
      } catch (error) {
        throw error;
      }
    },
    getYearsAndSemestersFromFiles (subcategoryKey) {
      const { files } = this.subcategoryFiles[subcategoryKey]
        ? this.subcategoryFiles[subcategoryKey]
        : { files: [] }
      const semesters = {};
      const years = [];

      files.forEach(({ year, semester }) => {
        if (year) {
          years.push(year);
        }

        if (semester) {
          if(semesters[year] &&  semesters[year].length) {
            semesters[year].push(semester)
          } else {
            semesters[year] = [semester]
          }
        }
      });

      return {
        years,
        semesters: Object.keys(semesters).length ? semesters : null
      }
    },
    async saveSubcategory (subcategoryId, subcategoryName, customParams) {
      try {
        const params = {
          name: subcategoryName,
          title: ICfilesName[integrityComplianceKey.integrity][subcategoryName],
          description: { ops: [] },
          ...customParams
        }

        await api.updateSubcategory(subcategoryId ,params)

      } catch (error) {
        throw error;
      }
    },
    async saveSubcategoriesFiles () {
      const subcategoriesName = Object.keys(this.subcategoryFiles);
      if (subcategoriesName.length) {
        for(const subcategoryKey of subcategoriesName) {
          const subcategoryFiles = this.subcategoryFiles[subcategoryKey];
          const { subcategoryId, files } = subcategoryFiles;
          let uploadedS3Files = [];

          if (files && files.length) { // there are new files to upload to S3
            uploadedS3Files = await this.uploadSubcategoryFilesToS3({
              subcategoryId,
              subcategoryKey,
              subcategoryFiles: [...files]
            });
            this.subcategoryFiles[subcategoryKey].files = []
          }

          await this.saveFiles(uploadedS3Files);
        }
      }
    },
    async uploadSubcategoryFilesToS3 (subcategoryData) {
      const { subcategoryId, subcategoryKey, subcategoryFiles } = subcategoryData;
      if (!subcategoryFiles.length) return;
      const uploadedS3Files = await Promise.all(
        subcategoryFiles.map(async(subcategoryFile) => {
          try {
            const { document_id, year } = subcategoryFile;
            const { Bucket, Key } = await s3.uploadFile(
              subcategoryFile.path,
              subcategoryFile.file
            );

            const params = {
              id: document_id,
              name: `${ICfilesName[integrityComplianceKey.integrity][subcategoryKey]} ${year}`,
              description: `${ICfilesName[integrityComplianceKey.integrity][subcategoryKey]} ${year}`,
              srcBucket: Bucket,
              src: Key,
              category_id: this.categoryData.id,
              subcategory_id: subcategoryId,
              year
            }

            if (subcategoryFile.semester) {
              params.semester = subcategoryFile.semester
            }

            return params;

          } catch (error) {
            console.error(error);
          }
        })
      )
      return uploadedS3Files;
    },
    async saveFiles (files) {
      for(const fileData of files) {
        try {
          if (fileData.id) {
            await api.updateDocument(fileData.id, fileData);
          } else {
            await api.createDocument(fileData);
          }
        } catch (error) {
          console.error(error)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.statistics {
  width: 100%;
  &__subcategory {
    margin-top: $global_padding;
  }
}

.input-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 20px 0px;

    label {
        @extend %audi-b;
    }
}
</style>
